import { api } from '@/lib/api';
import type { Word } from '@/types/word.types';

interface UpdateWordRequest {
  rating?: number;
}

interface UserWordResponse {
  success: boolean;
  data: {
    id: string;
    user_id: string;
    word_id: string;
    rating: number;
    created_at: string;
    updated_at: string;
  };
  message?: string;
}

interface UserWordsListResponse {
  ok: boolean;
  data: Array<{
    id: string;
    user_id: string;
    word_id: string;
    rating: number;
    created_at: string;
    updated_at: string;
    word: string;
    source: string;
    word_created_at: string;
    word_updated_at: string;
    payload: {
      data: Array<{
        word: string;
        phonetic?: string;
        phonetics?: Array<{
          text: string;
          audio?: string;
          license?: {
            name: string;
            url: string;
          };
          sourceUrl?: string;
        }>;
        meanings: Array<{
          partOfSpeech: string;
          definitions: Array<{
            definition: string;
            example?: string;
            synonyms?: string[];
            antonyms?: string[];
          }>;
          synonyms?: string[];
          antonyms?: string[];
        }>;
        license?: {
          name: string;
          url: string;
        };
        sourceUrls?: string[];
      }>;
    };
  }>;
  count: number;
}

export const userWordsService = {
  /**
   * Get all words in user's learning list
   */
  getUserWords: async (): Promise<Word[]> => {
    const response = await api.get<UserWordsListResponse>('/user-words');

    // Transform backend response to frontend Word type
    return response.data.map((item) => {
      const payloadData = item.payload.data;

      // Combine all meanings from all payload data entries
      const allMeanings = payloadData?.flatMap(wordData =>
        wordData.meanings?.map(meaning => ({
          partOfSpeech: meaning.partOfSpeech,
          meaning: meaning.definitions[0]?.definition || '',
          example: meaning.definitions[0]?.example,
          synonyms: meaning.synonyms,
          antonyms: meaning.antonyms,
          // Include ALL definitions for this part of speech
          definitions: meaning.definitions,
        })) || []
      ) || [];

      // Use the first wordData for phonetic and other metadata
      const firstWordData = payloadData?.[0];

      return {
        id: item.id,
        word: item.word,
        definitions: allMeanings,
        addedAt: new Date(item.created_at),
        rating: item.rating,
        // Additional dictionary API data
        phonetic: firstWordData?.phonetic,
        phonetics: firstWordData?.phonetics,
        sourceUrls: firstWordData?.sourceUrls,
      };
    });
  },

  /**
   * Add a word to user's learning list
   */
  addWord: async (word: string): Promise<UserWordResponse> => {
    const response = await api.post<UserWordResponse>('/user-words', {
      word,
      rating: 1, // Default rating for new words
    });
    return response;
  },

  /**
   * Update word progress (rating and/or learned status)
   */
  updateWord: async (word: string, updates: UpdateWordRequest): Promise<UserWordResponse> => {
    const response = await api.put<UserWordResponse>(`/user-words/${word}`, {
      rating: updates.rating,
    });
    return response;
  },

  /**
   * Remove word from learning list
   */
  deleteWord: async (word: string): Promise<void> => {
    await api.delete(`/user-words/${word}`);
  },

  /**
   * Search and filter words with sorting
   */
  searchWords: async (filters: {
    ratings?: number[];
    sortBy?: 'newest' | 'oldest' | 'alphabetical';
  }): Promise<Word[]> => {
    const params = new URLSearchParams();

    if (filters.ratings && filters.ratings.length > 0) {
      params.append('rating', filters.ratings.join(','));
    }


    if (filters.sortBy) {
      params.append('sort', filters.sortBy);
    }

    const queryString = params.toString();
    const endpoint = queryString ? `/user-words/search?${queryString}` : '/user-words/search';

    const response = await api.get<UserWordsListResponse>(endpoint);

    // Transform backend response to frontend Word type
    return response.data.map((item) => {
      const payloadData = item.payload.data;

      // Combine all meanings from all payload data entries
      const allMeanings = payloadData?.flatMap(wordData =>
        wordData.meanings?.map(meaning => ({
          partOfSpeech: meaning.partOfSpeech,
          meaning: meaning.definitions[0]?.definition || '',
          example: meaning.definitions[0]?.example,
          synonyms: meaning.synonyms,
          antonyms: meaning.antonyms,
          // Include ALL definitions for this part of speech
          definitions: meaning.definitions,
        })) || []
      ) || [];

      // Use the first wordData for phonetic and other metadata
      const firstWordData = payloadData?.[0];

      return {
        id: item.id,
        word: item.word,
        definitions: allMeanings,
        addedAt: new Date(item.created_at),
        rating: item.rating,
        // Additional dictionary API data
        phonetic: firstWordData?.phonetic,
        phonetics: firstWordData?.phonetics,
        sourceUrls: firstWordData?.sourceUrls,
      };
    });
  },
};
